import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { apiService, GoogleCalendarEvent, Provider, Procedure, Operatory } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

interface GoogleEventModalProps {
  event: GoogleCalendarEvent;
  onClose: () => void;
  onChange: () => void; // triggered after any backend mutation, lets parent reload
}

// Format a Google start/end object into local date+time inputs.
function fmtDateInput(g: { dateTime?: string; date?: string } | null | undefined) {
  if (!g) return '';
  const src = g.dateTime || g.date;
  if (!src) return '';
  return moment(src).format('YYYY-MM-DD');
}
function fmtTimeInput(g: { dateTime?: string; date?: string } | null | undefined) {
  if (!g) return '';
  if (!g.dateTime) return ''; // all-day event
  return moment(g.dateTime).format('HH:mm');
}

const GoogleEventModal: React.FC<GoogleEventModalProps> = ({ event, onClose, onChange }) => {
  const { addToast } = useToast();

  // Editable fields, prefilled from the event
  const [summary, setSummary] = useState(event.summary || '');
  const [description, setDescription] = useState(event.description || '');
  const [location, setLocation] = useState(event.location || '');
  const [date, setDate] = useState(fmtDateInput(event.start));
  const [startTime, setStartTime] = useState(fmtTimeInput(event.start));
  const [endTime, setEndTime] = useState(fmtTimeInput(event.end));
  const [busy, setBusy] = useState<null | 'save' | 'cancel' | 'confirm' | 'delete' | 'convert'>(null);

  // Convert-form state
  const [showConvert, setShowConvert] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [operatories, setOperatories] = useState<Operatory[]>([]);
  const [convertProvider, setConvertProvider] = useState<number | ''>('');
  const [convertProcedure, setConvertProcedure] = useState<number | ''>('');
  const [convertOperatory, setConvertOperatory] = useState<number | ''>('');
  const [convertPatientName, setConvertPatientName] = useState(event.summary || '');
  const [convertPatientEmail, setConvertPatientEmail] = useState('');
  const [convertPatientPhone, setConvertPatientPhone] = useState('');

  useEffect(() => {
    if (!showConvert) return;
    Promise.all([
      apiService.getProviders(true),
      apiService.getProcedures(true),
      apiService.getOperatories(true),
    ]).then(([p, pr, o]) => {
      setProviders(p.data.data || []);
      setProcedures(pr.data.data || []);
      setOperatories(o.data.data || []);
    }).catch((e) => {
      addToast(e?.response?.data?.message || 'Konnte Stammdaten nicht laden.', 'error');
    });
  }, [showConvert, addToast]);

  const isCancelled = event.status === 'cancelled';
  const isAllDay = useMemo(() => !event.start?.dateTime, [event]);

  const handleSave = async () => {
    if (!summary.trim()) {
      addToast('Titel darf nicht leer sein.', 'error');
      return;
    }
    setBusy('save');
    try {
      const partial: Partial<GoogleCalendarEvent> = {
        summary,
        description,
        location,
      };
      if (!isAllDay && date && startTime && endTime) {
        const startISO = moment.tz(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm', 'Europe/Madrid').toISOString();
        const endISO = moment.tz(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm', 'Europe/Madrid').toISOString();
        partial.start = { dateTime: startISO, timeZone: 'Europe/Madrid' };
        partial.end = { dateTime: endISO, timeZone: 'Europe/Madrid' };
      }
      const resp = await apiService.updateGoogleEvent(event.id, partial);
      if (resp.data?.success) {
        addToast('Event gespeichert.', 'success');
        onChange();
        onClose();
      } else {
        addToast(resp.data?.message || 'Speichern fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Event in Google Calendar stornieren? (Event bleibt sichtbar, wird durchgestrichen.)')) return;
    setBusy('cancel');
    try {
      const resp = await apiService.cancelGoogleEvent(event.id);
      if (resp.data?.success) {
        addToast('Event storniert.', 'success');
        onChange();
        onClose();
      } else addToast(resp.data?.message || 'Stornierung fehlgeschlagen.', 'error');
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Stornierung fehlgeschlagen.', 'error');
    } finally { setBusy(null); }
  };

  const handleConfirm = async () => {
    setBusy('confirm');
    try {
      const resp = await apiService.confirmGoogleEvent(event.id);
      if (resp.data?.success) {
        addToast('Event bestätigt.', 'success');
        onChange();
        onClose();
      } else addToast(resp.data?.message || 'Aktion fehlgeschlagen.', 'error');
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Aktion fehlgeschlagen.', 'error');
    } finally { setBusy(null); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Event aus Google Calendar UNWIDERRUFLICH löschen? Das kann nicht rückgängig gemacht werden.')) return;
    setBusy('delete');
    try {
      const resp = await apiService.deleteGoogleEvent(event.id);
      if (resp.data?.success) {
        addToast('Event gelöscht.', 'success');
        onChange();
        onClose();
      } else addToast(resp.data?.message || 'Löschen fehlgeschlagen.', 'error');
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Löschen fehlgeschlagen.', 'error');
    } finally { setBusy(null); }
  };

  const handleConvert = async () => {
    if (!convertProvider || !convertProcedure || !convertOperatory) {
      addToast('Bitte Behandler, Behandlung und Raum auswählen.', 'error');
      return;
    }
    setBusy('convert');
    try {
      const resp = await apiService.convertGoogleEventToAppointment(event.id, {
        provider_id: Number(convertProvider),
        procedure_id: Number(convertProcedure),
        operatory_id: Number(convertOperatory),
        patient_name: convertPatientName,
        patient_email: convertPatientEmail,
        patient_phone: convertPatientPhone,
      });
      if (resp.data?.success) {
        addToast('Als App-Termin übernommen.', 'success');
        onChange();
        onClose();
      } else addToast(resp.data?.message || 'Konvertierung fehlgeschlagen.', 'error');
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Konvertierung fehlgeschlagen.', 'error');
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-brand-surface rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-brand-border flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-semibold text-brand-text-muted">Google-Calendar-Event</div>
            <h2 className="text-lg font-bold text-brand-text mt-0.5">{event.summary || '(ohne Titel)'}</h2>
            <div className="text-xs text-brand-text-muted mt-1">
              {isCancelled && <span className="inline-block px-2 py-0.5 rounded bg-red-100 text-red-700 mr-2">storniert</span>}
              {event.appointmentId && (
                <span className="inline-block px-2 py-0.5 rounded bg-blue-100 text-blue-700">verknüpft mit App-Termin #{event.appointmentId}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-brand-text-muted hover:text-brand-text text-2xl leading-none" aria-label="Schließen">×</button>
        </div>

        <div className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">Titel</label>
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text focus:ring-2 focus:ring-brand-primary outline-none"
            />
          </div>

          {/* Date / time */}
          {isAllDay ? (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Ganztägiges Event — Zeiten können hier nicht editiert werden. Öffne es in Google Calendar.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">Datum</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">Start</label>
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">Ende</label>
                <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text" />
              </div>
            </div>
          )}

          {/* Location */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">Ort</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">Notizen</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text font-mono"
            />
          </div>

          {/* Meta */}
          <div className="text-[11px] text-brand-text-muted space-y-0.5">
            {event.creator?.email && <div>Erstellt von: {event.creator.email}</div>}
            {event.organizer?.email && event.organizer.email !== event.creator?.email && <div>Organisator: {event.organizer.email}</div>}
            {event.htmlLink && (
              <div>
                <a href={event.htmlLink} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline">
                  ↗ In Google Calendar öffnen
                </a>
              </div>
            )}
          </div>

          {/* Convert form (toggled) */}
          {showConvert && (
            <div className="border border-brand-border rounded p-3 bg-brand-surface-2 space-y-3">
              <div className="text-sm font-semibold text-brand-text">In App-Termin konvertieren</div>
              <p className="text-[11px] text-brand-text-muted">
                Verknüpft das Google-Event mit einem neuen App-Termin. Patient/Behandler/Behandlung müssen einmal zugeordnet werden.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <select value={convertProvider} onChange={(e) => setConvertProvider(e.target.value ? Number(e.target.value) : '')} className="p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text">
                  <option value="">Behandler…</option>
                  {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select value={convertProcedure} onChange={(e) => setConvertProcedure(e.target.value ? Number(e.target.value) : '')} className="p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text">
                  <option value="">Behandlung…</option>
                  {procedures.map(p => {
                    const name = typeof p.name === 'string' ? p.name : (p.name as any).de || (p.name as any).en || '?';
                    return <option key={p.id} value={p.id}>{name}</option>;
                  })}
                </select>
                <select value={convertOperatory} onChange={(e) => setConvertOperatory(e.target.value ? Number(e.target.value) : '')} className="p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text">
                  <option value="">Raum…</option>
                  {operatories.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input type="text" placeholder="Patient" value={convertPatientName} onChange={(e) => setConvertPatientName(e.target.value)} className="p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text" />
                <input type="email" placeholder="E-Mail (optional)" value={convertPatientEmail} onChange={(e) => setConvertPatientEmail(e.target.value)} className="p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text" />
                <input type="tel" placeholder="Telefon (optional)" value={convertPatientPhone} onChange={(e) => setConvertPatientPhone(e.target.value)} className="p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowConvert(false)} disabled={busy === 'convert'} className="px-3 py-1.5 text-sm border border-brand-border rounded hover:bg-brand-surface-2 text-brand-text">Abbrechen</button>
                <button onClick={handleConvert} disabled={busy === 'convert'} className="px-4 py-1.5 text-sm bg-brand-primary text-white rounded font-semibold disabled:opacity-50">
                  {busy === 'convert' ? 'Konvertiere…' : 'Konvertieren'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-brand-border flex flex-wrap justify-between items-center gap-2">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleDelete}
              disabled={!!busy}
              className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
            >
              {busy === 'delete' ? 'Lösche…' : '🗑 Löschen'}
            </button>
            {!event.appointmentId && !showConvert && (
              <button
                onClick={() => setShowConvert(true)}
                disabled={!!busy}
                className="px-3 py-1.5 text-sm border border-brand-border text-brand-text rounded hover:bg-brand-surface-2 disabled:opacity-50"
              >
                → In App-Termin
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {isCancelled ? (
              <button onClick={handleConfirm} disabled={!!busy} className="px-3 py-1.5 text-sm bg-green-600 text-white rounded font-semibold disabled:opacity-50">
                {busy === 'confirm' ? 'Bestätige…' : 'Wieder aktivieren'}
              </button>
            ) : (
              <button onClick={handleCancel} disabled={!!busy} className="px-3 py-1.5 text-sm border border-amber-300 text-amber-700 rounded hover:bg-amber-50 disabled:opacity-50">
                {busy === 'cancel' ? 'Storniere…' : 'Stornieren'}
              </button>
            )}
            <button onClick={onClose} className="px-3 py-1.5 text-sm border border-brand-border rounded text-brand-text hover:bg-brand-surface-2">
              Schließen
            </button>
            <button onClick={handleSave} disabled={!!busy} className="px-4 py-1.5 text-sm bg-brand-primary text-white rounded font-semibold disabled:opacity-50">
              {busy === 'save' ? 'Speichere…' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleEventModal;
